import { useEffect, useMemo, useState } from 'react'
import { X, Search, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useParents,
  useUpdateCampaignAssignment,
  type PortfolioParent,
  type PortfolioRole,
} from '@/api/portfolios'

interface CampaignAssignmentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  campaignId: number | null
  campaignName?: string
}

const ROLES: Array<{ id: PortfolioRole; label: string; hint: string }> = [
  { id: 'auto', label: 'Auto', hint: 'Auto-Targeting-Kampagne pro Child-ASIN' },
  { id: 'broad', label: 'Broad', hint: 'Portfolio-Kampagne, Broad-Match' },
  { id: 'exact', label: 'Exact', hint: 'Portfolio-Kampagne, Exact-Match' },
  { id: 'pat', label: 'PAT', hint: 'Product-Attribute-Targeting' },
  { id: 'migration_source', label: 'Migration Source', hint: 'Legacy-Kampagne, aus der migriert wurde' },
]

export default function CampaignAssignmentDialog({
  open,
  onOpenChange,
  campaignId,
  campaignName,
}: CampaignAssignmentDialogProps) {
  const { data: parents, isLoading } = useParents()
  const update = useUpdateCampaignAssignment()

  const [query, setQuery] = useState('')
  const [selectedParentId, setSelectedParentId] = useState<number | null>(null)
  const [role, setRole] = useState<PortfolioRole>('broad')
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setQuery('')
      setSelectedParentId(null)
      setRole('broad')
      setSubmitError(null)
    }
  }, [open, campaignId])

  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onOpenChange(false)
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onOpenChange])

  const filtered = useMemo(() => {
    const list = parents ?? []
    const q = query.trim().toLowerCase()
    if (!q) return list
    return list.filter(
      (p) =>
        p.amazonParentAsin.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        (p.ppcCode ?? '').toLowerCase().includes(q),
    )
  }, [parents, query])

  if (!open) return null

  async function handleSubmit() {
    if (campaignId == null || selectedParentId == null) {
      setSubmitError('Bitte Parent auswählen')
      return
    }
    setSubmitError(null)
    try {
      await update.mutateAsync({
        campaignId,
        parentId: selectedParentId,
        role,
      })
      onOpenChange(false)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unbekannter Fehler')
    }
  }

  const selected = parents?.find((p) => p.id === selectedParentId) ?? null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="w-full max-w-lg rounded-lg border border-[var(--border)] bg-[var(--card)] shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-base font-semibold">Kampagne zuordnen</h2>
            <p className="truncate text-xs text-[var(--muted-foreground)]">
              {campaignName ?? `Campaign-ID ${campaignId ?? '—'}`}
            </p>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="rounded-md p-1 text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-4 py-4">
          {/* Parent-Suche */}
          <div>
            <label className="mb-1 block text-xs font-medium">Parent</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-foreground)]" />
              <input
                type="text"
                placeholder="ASIN, Name oder PPC-Code suchen..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-9 w-full rounded-md border border-[var(--border)] bg-[var(--background)] pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-[var(--ring)]"
              />
            </div>
            <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-[var(--border)]">
              {isLoading ? (
                <div className="py-4 text-center text-xs text-[var(--muted-foreground)]">Laden...</div>
              ) : filtered.length === 0 ? (
                <div className="py-4 text-center text-xs text-[var(--muted-foreground)]">
                  Keine Treffer
                </div>
              ) : (
                filtered.map((p) => (
                  <ParentRow
                    key={p.id}
                    parent={p}
                    selected={p.id === selectedParentId}
                    onClick={() => setSelectedParentId(p.id)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Role */}
          <div>
            <label className="mb-1 block text-xs font-medium">Rolle</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as PortfolioRole)}
              className="h-9 w-full rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm outline-none focus:ring-2 focus:ring-[var(--ring)]"
            >
              {ROLES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label} — {r.hint}
                </option>
              ))}
            </select>
          </div>

          {submitError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {submitError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-[var(--border)] px-4 py-3">
          <div className="text-xs text-[var(--muted-foreground)] truncate">
            {selected ? (
              <>
                Ziel:{' '}
                <span className="font-mono">{selected.amazonParentAsin}</span> · {role}
              </>
            ) : (
              'Kein Parent ausgewählt'
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onOpenChange(false)}
              className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--muted)]"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSubmit}
              disabled={selectedParentId == null || update.isPending}
              className={cn(
                'rounded-md bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-zinc-800 disabled:opacity-40',
              )}
            >
              {update.isPending ? 'Speichere...' : 'Zuordnen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function ParentRow({
  parent,
  selected,
  onClick,
}: {
  parent: PortfolioParent
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between border-b border-[var(--border)] px-3 py-2 text-left text-sm last:border-b-0 hover:bg-[var(--muted)]',
        selected && 'bg-[var(--accent)]',
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-mono text-xs">{parent.amazonParentAsin}</span>
          {parent.ppcCode && (
            <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-zinc-700">
              {parent.ppcCode}
            </span>
          )}
        </div>
        <div className="truncate text-xs text-[var(--muted-foreground)]">{parent.name}</div>
      </div>
      {selected && <Check className="ml-2 h-4 w-4 text-emerald-600 shrink-0" />}
    </button>
  )
}
